import React, { useEffect } from 'react';
import {
  ComboBox,
  ComboBoxFilterChangeEvent,
  ComboBoxProps
} from '@progress/kendo-react-dropdowns';
import { filterBy, FilterDescriptor } from '@progress/kendo-data-query';
import { Error } from '@progress/kendo-react-labels';
import cloneDeep from 'lodash.clonedeep';
const delay: number = 500;

interface OnDemandTaskComboBoxProps extends ComboBoxProps {
  category: string[];
  onRemoveODTCategory: (index: number) => void;
}

export const OnDemandTaskComboBox = (props: OnDemandTaskComboBoxProps) => {
  const onDemandTasks = React.useRef<string[] | undefined>(props.category);
  const [data, setData] = React.useState<string[]>(props.category);
  const [loading, setLoading] = React.useState(false);
  const [filter, setFilter] = React.useState<string>('');

  useEffect(() => {
    if (onDemandTasks.current) {
      setData(onDemandTasks.current);
      setLoading(false);
      return;
    }

    onDemandTasks.current = [...new Set(data)];
  }, []);

  const filterData = (filter: FilterDescriptor) => {
    const data = onDemandTasks.current!.slice();
    return filterBy(data, filter);
  };

  // debounce 500ms so filtering doesnt take affect after every keystroke
  const timeout = React.useRef<any>(null);
  const filterChange = (event: ComboBoxFilterChangeEvent) => {
    clearTimeout(timeout.current);

    setFilter(event.filter.value || '');

    timeout.current = setTimeout(() => {
      const filteredData = filterData(event.filter);

      // Add custom entry as the first option when filter has value and no exact match found
      if (
        event.filter.value &&
        !filteredData.some(
          (item) =>
            item.toLowerCase().trim() ===
            event.filter.value.toLowerCase().trim()
        )
      ) {
        setData([event.filter.value, ...filteredData]);
      } else {
        setData(filteredData);
      }

      setLoading(false);
    }, delay);
    setLoading(true);
  };

  const customItemRender = (li: React.ReactElement, itemProps: any) => {
    const index = itemProps.index;
    const itemText = itemProps.dataItem;
    const filteredData = filterData({
      field: undefined,
      operator: 'contains',
      ignoreCase: true,
      value: itemText
    });

    // The first item is our custom entry if it matches the filter
    if (
      index === 0 &&
      filter &&
      itemText.trim() === filter.trim() &&
      !filteredData.some(
        (item) => item.toLowerCase().trim() === itemText.toLowerCase().trim()
      )
    ) {
      return React.cloneElement(
        li,
        li.props as React.HTMLAttributes<HTMLLIElement>,
        <div
          className={'w-full'}
          onClick={() => {
            setFilter('');
            if (onDemandTasks.current) {
              setData([...onDemandTasks.current, filter]);
            }
          }}>
          <span>
            <strong>Add:</strong> {itemText}
          </span>
        </div>
      );
    }

    return React.cloneElement(
      li,
      li.props as React.HTMLAttributes<HTMLLIElement>,
      <div className={'w-full d-flex justify-content-between'}>
        <span>{itemText}</span>
        <span
          className={'material-symbols-outlined text-danger'}
          onClick={(e) => {
            e.stopPropagation();
            props.onRemoveODTCategory(index);
            const result = cloneDeep(data);
            result.splice(index, 1);
            onDemandTasks.current = result;
            setData(result);
          }}>
          delete
        </span>
      </div>
    );
  };

  return (
    <>
      <ComboBox
        placeholder={'Select or type to add new category'}
        data={data}
        filterable={true}
        onFilterChange={filterChange}
        loading={loading}
        itemRender={customItemRender}
        {...props}
      />
      {props?.validationMessage && <Error>{props.validationMessage}</Error>}
    </>
  );
};

export default OnDemandTaskComboBox;
